import { Component, OnInit, Inject } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { PageableTableComponent } from '../../../shared/controls/pageable-table/pageable-table.component';
import { AutoCompleteDropdownComponent } from '../../../shared/controls/auto-complete-dropdown/auto-complete-dropdown.component';

import { DialogService } from '../../../shared/controls/message-dialog/message-dialog.service';
import { ToastService } from '../../../shared/toast/toast.service';
import { ErrorService } from '../../../shared/services/error-service';
import { InitializerService } from '../../../shared/services/initializer-service';
import { AuthService } from '../../../shared/services/auth.service';
import { LoadingService } from '../../../shared/services/loading.service';
import { LoadingHelper } from '../../../shared/services/loading-helper';

import { HasPermissionDirective } from '../../../shared/directives/has-permission.directive';
import { AreaConstants } from '../../../shared/constants/area.constants';
import { PermissionConstants } from '../../../shared/constants/permission.constants';

import { AgeingService, PeriodService, AgeingBatchItem, PagingData, AllPeriodItem } from '../../../../service/proxy';

@Component({
    templateUrl: './ageing-list.component.html',
    styleUrls: ['./ageing-list.component.scss'],

    imports: [
        CommonModule,
        FormsModule,
        PageableTableComponent,
        AutoCompleteDropdownComponent,
        HasPermissionDirective,
    ],

    providers: [
        AgeingService,
        PeriodService,
    ]
})
export class AgeingListComponent implements OnInit {
    public initialized = false;

    public selectedStatus: boolean = true;
    public selectedPeriodId: number = 0;
    public search: string = '';

    public paging: PagingData = new PagingData();
    public periods: AllPeriodItem[] = [];
    public batches: AgeingBatchItem[] = [];

    public statusOptions = [
        { id: 0, label: 'Pending' },
        { id: 1, label: 'Completed' }
    ];

    public showEdit: boolean = false;
    public showDelete: boolean = false;

    constructor(
        public authService: AuthService,
        private router: Router,
        private ageingService: AgeingService,
        private initializerService: InitializerService,
        private periodService: PeriodService,
        private toastService: ToastService,
        private errorService: ErrorService,
        private dialogService: DialogService,
        private loadingService: LoadingService)
    {
        this.paging = this.initializerService.createPagingData();
    }

    ngOnInit(): void {
        LoadingHelper.withLoading(
            this.periodService.getAllPeriods(),
            this.loadingService
        ).subscribe((data) => {
            this.initialized = true;
            this.periods = data.results || [];
            this.selectedPeriodId = this.periods[1]?.id ?? 0;

            this.refreshGridData();
            this.initializePermissions();
            this.refreshGridData();
        });
    }

    initializePermissions(): void {
        this.showEdit =
            this.selectedStatus === false && this.authService.hasPermission(AreaConstants.Ageing, PermissionConstants.Import);

        this.showDelete =
            (this.selectedStatus === false && this.authService.hasPermission(AreaConstants.Ageing, PermissionConstants.DeletePending)) ||
            (this.selectedStatus === true && this.authService.hasPermission(AreaConstants.Ageing, PermissionConstants.DeleteCompleted));
    }

    formatPeriod = (period: AllPeriodItem): string =>
        `${period?.year}-${String(period?.month).padStart(2, '0')}`;

    displayStatus = (status: any): string => status?.label ?? '';

    onStatusSelectionChange(statusId: number | null): void {
        this.selectedStatus = statusId === 1;
        this.refreshGridData();
        this.initializePermissions();
    }

    onPeriodSelectionChange(periodId: number | null): void {
        this.selectedPeriodId = periodId ?? 0;
        this.refreshGridData();
    }

    getColumnMap(): Record<string, string> {
        const base: Record<string, string> = {
            createdAt: 'Date Created',
            fileName: 'Filename',
            affectedPolicies: 'Client Policies',
            clientName: 'Client',
            period: 'Period',
            isAgeingMatched: 'Matched'
        };

        return this.selectedStatus
            ? { ...base, completedAt: 'Date Completed' }
            : { ...base, updatedAt: 'Date Updated' };
    }

    refreshGridData(): void {
        this.paging = this.initializerService.createPagingData();
        this.getAgeingBatches(this.paging);
    }

    getAgeingBatches(pagingData: PagingData = new PagingData): void {
        if (!this.initialized) {
            return;
        }

        const search = `${this.selectedPeriodId}|${this.search}`;

        LoadingHelper.withLoading(
            this.ageingService.getAgeingBatches(this.selectedStatus, search, pagingData.pageNo, pagingData.pageSize, pagingData.sortDirection, pagingData.sortColumn),
            this.loadingService
        ).subscribe({
            next: (data) => {
                this.paging = data.pagingData;
                this.batches = data.results || [];
            },
            error: (error) => {
                if (error.status === 204) {
                    this.batches = [];
                    this.paging = this.initializerService.createPagingData();
                }
                else {
                    this.errorService.handleError(error);
                }
            }
        });
    }

    createAgeingBatch(): void {
        this.router.navigate([`/ageing-create`]);
    }

    editAgeingBatch(batchId: number): void {
        sessionStorage.setItem('isRedirected', true.toString());
        this.router.navigate([`/ageing-wizard/${batchId}`]);
    }

    deleteAgeingBatch(batchId: number): void {
        const title = this.selectedStatus
            ? 'Delete COMPLETED Batch'
            : 'Delete Pending Batch';

        const message = this.selectedStatus
            ? 'You are about to delete a COMPLETED batch. Are you sure?'
            : 'Are you sure you want to delete this batch?';

        const toastMessage = this.selectedStatus
            ? 'Completed batch deleted successfully.'
            : 'Pending batch deleted successfully.';

        if (!this.selectedStatus) {
            this.deletePendingAgeingBatch(batchId, title, message, toastMessage);
        }
        else {
            this.deleteCompletedAgeingBatch(batchId, title, message, toastMessage);
        };
    }

    deletePendingAgeingBatch(batchId: number, title: string, message: string, toastMessage: string): void {
        this.dialogService.show(title, message, 'Warning')
            .subscribe((confirmed: boolean) => {
                if (confirmed) {
                    LoadingHelper.withLoading(
                        this.ageingService.deletePendingAgeingBatch(batchId),
                        this.loadingService
                    ).subscribe({
                        next: () => {
                            this.getAgeingBatches(this.paging);
                        },
                        error: (error: any) => {
                            this.errorService.handleError(error);
                        },
                        complete: () => {
                            this.toastService.show(toastMessage, 'success');
                        }
                    });
                }
            }
        );
    }

    deleteCompletedAgeingBatch(batchId: number, title: string, message: string, toastMessage: string): void {
        this.dialogService.show(title, message, 'Warning')
            .subscribe((confirmed: boolean) => {
                if (confirmed) {
                    LoadingHelper.withLoading(
                        this.ageingService.deleteCompletedAgeingBatch(batchId),
                        this.loadingService
                    ).subscribe({
                        next: () => {
                            this.getAgeingBatches(this.paging);
                        },
                        error: (error: any) => {
                            if (error.status === 409) {
                                this.toastService.show('This batch is matched and cannot be deleted.', 'warning');
                            }
                            else {
                                this.errorService.handleError(error);
                            }
                        },
                        complete: () => {
                            this.toastService.show(toastMessage, 'success');
                        }
                    });
                }
            }
        );
    }
}
